/**
 * CTEStepPanel — Edit the cte_steps section of a job.
 *
 * One card per CTE step: step_name (monospace inline), sql_body (monospace
 * textarea), description. The "final" step_name is rejected client-side with
 * an inline error (not a toast). Changes call PATCH .../cte-steps.
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import type {
  PEJob,
  CTEStep,
  CTEStepsPayload,
  CTESuggestionStep,
} from "../../../features/pipeline-engineer/types";
import { patchCTESteps } from "../../../features/pipeline-engineer/api/pe-client";
import { CTEAIPanel } from "./CTEAIPanel";

// ---------------------------------------------------------------------------
// Internal row type
// ---------------------------------------------------------------------------

interface StepRow {
  id: string;
  stepName: string;
  sqlBody: string;
  description: string;
  /** Client-side validation error for step_name */
  nameError: string | null;
}

function stepToRow(s: CTEStep, idx: number): StepRow {
  return {
    id: `cte-${idx}-${s.stepName}`,
    stepName: s.stepName,
    sqlBody: s.sqlBody,
    description: s.description ?? "",
    nameError: null,
  };
}

function rowsToPayload(rows: StepRow[]): CTEStepsPayload {
  return {
    cte_steps: rows.map((r) => ({
      step_name: r.stepName,
      sql_body: r.sqlBody,
      description: r.description || null,
    })),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

// Compiled at module level (not inside a render/hook) — SM learning 2.4.
const STEP_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

function validateStepName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === "") return null; // empty is allowed (incomplete, not invalid yet)
  if (trimmed.toLowerCase() === "final") {
    return "'final' is reserved — it is auto-generated from column mappings";
  }
  if (!STEP_NAME_RE.test(trimmed)) {
    return "Must be a valid SQL identifier: letters, digits, underscore only; cannot start with a digit";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CTEStepPanelProps {
  job: PEJob;
  /** Project ID — passed to CTEAIPanel for LLM provider resolution. */
  projectId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CTEStepPanel({
  job,
  projectId,
  onSaved,
  onDirtyChange,
  onConflict,
}: CTEStepPanelProps) {
  const [rows, setRows] = useState<StepRow[]>(() =>
    job.cteSteps.map(stepToRow),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Compare rows (sans local `id` and `nameError`) against stepToRow output —
  // both camelCase to avoid permanent-dirty from camelCase vs snake_case mismatch.
  useEffect(() => {
    const pick = (r: StepRow) => ({
      stepName: r.stepName,
      sqlBody: r.sqlBody,
      description: r.description || null,
    });
    const original = JSON.stringify(job.cteSteps.map(stepToRow).map(pick));
    const current = JSON.stringify(rows.map(pick));
    onDirtyChange(original !== current);
  }, [rows, job.cteSteps, onDirtyChange]);

  useEffect(() => {
    setRows(job.cteSteps.map(stepToRow));
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = useCallback((id: string, patch: Partial<StepRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, ...patch };
        // Re-validate step name whenever it changes
        if ("stepName" in patch) {
          updated.nameError = validateStepName(updated.stepName);
        }
        return updated;
      }),
    );
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        id: `cte-new-${Date.now()}`,
        stepName: "",
        sqlBody: "",
        description: "",
        nameError: null,
      },
    ]);
  }, []);

  const hasNameErrors = rows.some((r) => r.nameError !== null);

  // ── AI panel callbacks ─────────────────────────────────────────────────────

  /**
   * Appends an AI-suggested CTE step to the list.
   * Called from CTEAIPanel when user clicks "Apply" on a suggestion.
   */
  const handleApplyStep = useCallback((step: CTESuggestionStep) => {
    const nameError = validateStepName(step.stepName);
    setRows((prev) => [
      ...prev,
      {
        id: `cte-ai-${Date.now()}-${step.stepName}`,
        stepName: step.stepName,
        sqlBody: step.sqlBody,
        description: step.description ?? "",
        nameError,
      },
    ]);
  }, []);

  /**
   * Updates a step's SQL body in-place after AI refinement.
   * Called from CTEAIPanel when user accepts a refined SQL suggestion.
   * Does NOT save automatically — user still clicks Save CTE Steps.
   */
  const handleStepRefined = useCallback((stepName: string, newSql: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.stepName === stepName ? { ...r, sqlBody: newSql } : r,
      ),
    );
  }, []);

  // Build a CTEStep[] view of current saved rows for the AI panel context.
  // Filter out rows with empty stepName — unsaved/incomplete rows would send
  // malformed context to the refine-cte endpoint and cause confusing LLM output.
  const currentCteSteps: CTEStep[] = rows
    .filter((r) => r.stepName.trim() !== "")
    .map((r) => ({
      stepName: r.stepName.trim(),
      sqlBody: r.sqlBody,
      description: r.description || null,
    }));

  const handleSave = useCallback(async () => {
    if (hasNameErrors) return;
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await patchCTESteps(
        job.pipelineId,
        job.id,
        rowsToPayload(rows),
      );
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [rows, hasNameErrors, job.pipelineId, job.id, onSaved, onConflict]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-white/40">
        CTE steps are woven into the generated SQL in the order listed. The{" "}
        <code className="font-mono text-white/60">final</code> step is always
        auto-generated from column mappings.
      </p>

      {/* AI sidebar — suggest and refine CTE steps */}
      <CTEAIPanel
        jobId={job.id}
        projectId={projectId}
        currentCteSteps={currentCteSteps}
        onApplyStep={handleApplyStep}
        onStepRefined={handleStepRefined}
      />

      {rows.map((row, idx) => (
        <div
          key={row.id}
          className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
        >
          {/* Header */}
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center h-5 w-5 rounded-full bg-brand-500/20 text-brand-400 text-xs font-semibold">
              {idx + 1}
            </span>
            <span className="text-xs text-white/40 flex-1">CTE Step</span>
            <button
              type="button"
              aria-label={`Remove CTE step ${idx + 1}`}
              onClick={() => removeRow(row.id)}
              className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          </div>

          {/* Step name */}
          <div>
            <label htmlFor={`cte-name-${row.id}`} className={LABEL_CLASS}>
              Step Name (SQL identifier)
            </label>
            <input
              id={`cte-name-${row.id}`}
              type="text"
              className={`${INPUT_CLASS} font-mono ${row.nameError !== null ? "border-red-500/60 focus:border-red-500/60 focus:ring-red-500/30" : ""}`}
              placeholder="e.g. base_data"
              value={row.stepName}
              onChange={(e) => updateRow(row.id, { stepName: e.target.value })}
            />
            {/* Inline error — not a toast (§15.1 AC6) */}
            {row.nameError !== null && (
              <p className="text-red-400 text-xs mt-1" role="alert">
                {row.nameError}
              </p>
            )}
          </div>

          {/* SQL body */}
          <div>
            <label htmlFor={`cte-sql-${row.id}`} className={LABEL_CLASS}>
              SQL Body
            </label>
            <textarea
              id={`cte-sql-${row.id}`}
              rows={5}
              className={`${INPUT_CLASS} font-mono text-xs resize-y`}
              placeholder="SELECT o.id, o.customer_id FROM orders o"
              value={row.sqlBody}
              onChange={(e) => updateRow(row.id, { sqlBody: e.target.value })}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor={`cte-desc-${row.id}`} className={LABEL_CLASS}>
              Description (optional)
            </label>
            <input
              id={`cte-desc-${row.id}`}
              type="text"
              className={INPUT_CLASS}
              placeholder="What this CTE does"
              value={row.description}
              onChange={(e) =>
                updateRow(row.id, { description: e.target.value })
              }
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors w-full justify-center"
      >
        <Plus size={13} aria-hidden="true" />
        Add Step
      </button>

      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || hasNameErrors}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          )}
          Save CTE Steps
        </button>
      </div>
    </div>
  );
}
